import { useContext } from "react";
import { Box, Typography, Paper, keyframes } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { LayoutContext } from "../../context/LayoutContext";
import { NotificationDropdown, QuickActionsDropdown } from "./components";

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const StatCard = ({ label, value, color, delay = 0, collapsed }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        minWidth: collapsed ? 120 : 100,
        p: collapsed ? 1.5 : 1,
        py: 1.5,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "grey.200",
        backgroundColor: "#fff",
        cursor: "pointer",
        animation: `${fadeInUp} 0.5s ease-out ${delay}s both`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 24px rgba(0,0,0,0.08)",
          borderColor: color || "grey.300",
          "& .arrow-icon": {
            opacity: 1,
            transform: "translateX(0)",
          },
        },
      }}
    >
      <Box>
        <Typography
          sx={{
            color: "text.secondary",
            fontWeight: 500,
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: 0.3,
            mb: 0.75,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 26,
            color: color || "text.primary",
            lineHeight: 1,
          }}
        >
          {value}
        </Typography>
      </Box>
      <ChevronRightIcon
        className="arrow-icon"
        sx={{
          color: "grey.400",
          fontSize: 22,
          opacity: 0.6,
          transition: "all 0.3s ease",
        }}
      />
    </Paper>
  );
};

const HeaderTopCards = ({ stats, apiStats, onQuickAction }) => {
  const { collapsed } = useContext(LayoutContext);

  const totalLeads = apiStats?.totalLeads ?? stats?.totalLeads ?? 0;
  const activeConsults = apiStats?.activeConsults ?? stats?.activeConsults ?? 0;
  const pendingFollowups = apiStats?.pendingFollowups ?? stats?.pendingFollowups ?? 0;

  const cardData = [
    { label: "Total Leads", value: totalLeads, color: null },
    { label: "Active Consults", value: activeConsults, color: null },
    { label: "Conv. Rate %", value: stats?.convRate || "0%", color: "#22C55E" },
    { label: "Revenue (MTD)", value: stats?.revenue || "₹0", color: null },
    { label: "Pending Follow-ups", value: pendingFollowups, color: "#F59E0B" },
  ];

  return (
    <Box sx={{ mb: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          px: 2,
          mb: 2.5,
          animation: `${slideInRight} 0.4s ease-out`,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            color: "text.primary",
            fontSize: 18,
          }}
        >
          CRM Performance & Priority Dashboard
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <NotificationDropdown />
          <QuickActionsDropdown onActionClick={onQuickAction} />
        </Box>
      </Box>

      {/* Stat Cards */}
      <Box
        sx={{
          display: "flex",
          gap: collapsed ? 1.5 : 2,
          flexWrap: { xs: "wrap", md: collapsed ? "nowrap" : "wrap", lg: "nowrap" },
          transition: "all 0.3s ease",
          px: 2,
        }}
      >
        {cardData.map((card, index) => (
          <StatCard
            key={card.label}
            label={card.label}
            value={card.value}
            color={card.color}
            delay={index * 0.1}
            collapsed={collapsed}
          />
        ))}
      </Box>
    </Box>
  );
};

export default HeaderTopCards;
